import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import { stockService, productionService, shipmentService, projectService } from '../services/api.service';
import { canCreate, MODULES } from '../utils/permissions';

const HomePage = ({ user }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [projects, setProjects] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [projectsWithParts, setProjectsWithParts] = useState({});

  useEffect(() => {
    loadProjects();
    loadActiveProjectsWithParts();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAll();
      setProjects(data);
    } catch (error) {
      console.error('Projeler yüklenemedi:', error);
    }
  };

  const loadActiveProjectsWithParts = async () => {
    try {
      const data = await projectService.getActiveWithParts();
      setProjectsWithParts(data);
    } catch (error) {
      console.error('Aktif projeler yüklenemedi:', error);
    }
  };

  // Sevkiyat (Lojistik) Formu
  const [sevkForm, setSevkForm] = useState({
    proje: '',
    proje_parcasi: '',
    tarih: new Date().toISOString().split('T')[0],
    parca_adedi: '',
    cikis_yeri: 'Fabrika',
    sevk_yeri: '',
    irsaliye_numarasi: '',
    agirlik: '',
    boy: '',
    hammadde: '',
    malzeme_adi: '',
    malzeme_turu: '',
    uzunluk: '',
    tonaj: ''
  });

  // Auto-fill malzeme_turu, uzunluk, and calculate tonaj for Sevk form
  useEffect(() => {
    if (sevkForm.proje && sevkForm.proje_parcasi) {
      const parts = projectsWithParts[sevkForm.proje] || [];
      const selectedPart = parts.find(p => p.part_name === sevkForm.proje_parcasi);
      
      if (selectedPart) {
        // Auto-fill malzeme_turu and uzunluk
        setSevkForm(prev => ({ 
          ...prev, 
          malzeme_turu: selectedPart.malzeme_turu || '',
          uzunluk: selectedPart.uzunluk || ''
        }));
        
        // Calculate tonaj in kg (not tons)
        if (selectedPart.unit_weight && sevkForm.parca_adedi) {
          const calculatedTonaj = selectedPart.unit_weight * parseFloat(sevkForm.parca_adedi); // In kg
          setSevkForm(prev => ({ ...prev, tonaj: calculatedTonaj.toFixed(2) }));
        }
      }
    }
  }, [sevkForm.proje, sevkForm.proje_parcasi, sevkForm.parca_adedi, projectsWithParts]);

  const handleSevkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const project = projects.find(p => p.name === sevkForm.proje);
      await shipmentService.create({
        project_id: project?.id || null,
        project_name: sevkForm.proje,
        part_name: sevkForm.proje_parcasi,
        material_type: sevkForm.malzeme_turu,
        length: sevkForm.uzunluk,
        quantity: parseInt(sevkForm.parca_adedi) || 0,
        shipment_date: new Date(sevkForm.tarih).toISOString(),
        source_location: sevkForm.cikis_yeri,
        destination: sevkForm.sevk_yeri,
        invoice_no: sevkForm.irsaliye_numarasi,
        total_weight: parseFloat(sevkForm.tonaj) || 0,
        notes: sevkForm.hammadde ? `Hammadde: ${sevkForm.hammadde}` : ''
      });
      setMessage({ type: 'success', text: '✅ Lojistik kaydı başarıyla eklendi!' });
      setSevkForm({
        proje: '',
        proje_parcasi: '',
        tarih: new Date().toISOString().split('T')[0],
        parca_adedi: '',
        cikis_yeri: 'Fabrika',
        sevk_yeri: '',
        irsaliye_numarasi: '',
        agirlik: '',
        boy: '',
        hammadde: '',
        malzeme_adi: '',
        malzeme_turu: '',
        uzunluk: '',
        tonaj: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Kayıt başarısız!' });
    } finally {
      setLoading(false);
    }
  };

  // Üretim Formu
  const [uretimForm, setUretimForm] = useState({
    proje: '',
    proje_parcasi: '',
    tarih: new Date().toISOString().split('T')[0],
    parca_adedi: '',
    rulo_numarasi: '',
    kanban_numarasi: '',
    operator: '',
    agirlik: '',
    boy: '',
    hammadde: '',
    malzeme_adi: '',
    malzeme_turu: '',
    uzunluk: '',
    tonaj: ''
  });

  // Auto-fill malzeme_turu, uzunluk, and calculate tonaj for Uretim form
  useEffect(() => {
    if (uretimForm.proje && uretimForm.proje_parcasi) {
      const parts = projectsWithParts[uretimForm.proje] || [];
      const selectedPart = parts.find(p => p.part_name === uretimForm.proje_parcasi);
      
      if (selectedPart) {
        // Auto-fill malzeme_turu and uzunluk
        setUretimForm(prev => ({ 
          ...prev, 
          malzeme_turu: selectedPart.malzeme_turu || '',
          uzunluk: selectedPart.uzunluk || ''
        }));
        
        // Calculate tonaj in kg (not tons)
        if (selectedPart.unit_weight && uretimForm.parca_adedi) {
          const calculatedTonaj = selectedPart.unit_weight * parseFloat(uretimForm.parca_adedi); // In kg
          setUretimForm(prev => ({ ...prev, tonaj: calculatedTonaj.toFixed(2) }));
        }
      }
    }
  }, [uretimForm.proje, uretimForm.proje_parcasi, uretimForm.parca_adedi, projectsWithParts]);

  const handleUretimSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const project = projects.find(p => p.name === uretimForm.proje);
      await productionService.create({
        project_id: project?.id || null,
        project_name: uretimForm.proje,
        part_name: uretimForm.proje_parcasi,
        material_type: uretimForm.malzeme_turu,
        length: uretimForm.uzunluk,
        quantity: parseInt(uretimForm.parca_adedi),
        production_date: new Date(uretimForm.tarih).toISOString(),
        operator: uretimForm.operator,
        rulo_numarasi: uretimForm.rulo_numarasi,
        kanban_numarasi: uretimForm.kanban_numarasi,
        weight: parseFloat(uretimForm.tonaj) || 0
      });
      setMessage({ type: 'success', text: '✅ Üretim kaydı başarıyla eklendi!' });
      setUretimForm({
        proje: '',
        proje_parcasi: '',
        tarih: new Date().toISOString().split('T')[0],
        parca_adedi: '',
        rulo_numarasi: '',
        kanban_numarasi: '',
        operator: '',
        agirlik: '',
        boy: '',
        hammadde: '',
        malzeme_adi: '',
        malzeme_turu: '',
        uzunluk: '',
        tonaj: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Kayıt başarısız!' });
    } finally {
      setLoading(false);
    }
  };

  // Stok Giriş Formu
  const [stokGirisForm, setStokGirisForm] = useState({
    malzeme_adi: '',
    tarih: new Date().toISOString().split('T')[0],
    siparis_numarasi: '',
    rulo_numarasi: '',
    irsaliye_numarasi: '',
    miktar: '',
    sirket_adi: '',
    fason_firma: ''
  });

  const handleStokGirisSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await stockService.create({
        material_name: stokGirisForm.malzeme_adi,
        material_code: stokGirisForm.siparis_numarasi,
        quantity: parseFloat(stokGirisForm.miktar),
        unit: 'kg',
        total_weight: parseFloat(stokGirisForm.miktar),
        location: stokGirisForm.sirket_adi,
        supplier: stokGirisForm.fason_firma,
        entry_type: 'in',
        rulo_numarasi: stokGirisForm.rulo_numarasi,
        irsaliye_numarasi: stokGirisForm.irsaliye_numarasi
      });
      setMessage({ type: 'success', text: '✅ Stok girişi başarıyla kaydedildi!' });
      setStokGirisForm({
        malzeme_adi: '',
        tarih: new Date().toISOString().split('T')[0],
        siparis_numarasi: '',
        rulo_numarasi: '',
        irsaliye_numarasi: '',
        miktar: '',
        sirket_adi: '',
        fason_firma: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Kayıt başarısız!' });
    } finally {
      setLoading(false);
    }
  };

  // Stok Çıkış Formu
  const [stokCikisForm, setStokCikisForm] = useState({
    malzeme_adi: '',
    rulo_numarasi: '',
    tarih: new Date().toISOString().split('T')[0],
    miktar: '',
    heat_numarasi: ''
  });

  const handleStokCikisSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await stockService.create({
        material_name: stokCikisForm.malzeme_adi,
        material_code: stokCikisForm.heat_numarasi,
        quantity: parseFloat(stokCikisForm.miktar),
        unit: 'kg',
        total_weight: parseFloat(stokCikisForm.miktar),
        location: 'Fabrika',
        entry_type: 'out',
        rulo_numarasi: stokCikisForm.rulo_numarasi,
        heat_numarasi: stokCikisForm.heat_numarasi
      });
      setMessage({ type: 'success', text: '✅ Stok çıkışı başarıyla kaydedildi!' });
      setStokCikisForm({
        malzeme_adi: '',
        rulo_numarasi: '',
        tarih: new Date().toISOString().split('T')[0],
        miktar: '',
        heat_numarasi: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Kayıt başarısız!' });
    } finally {
      setLoading(false);
    }
  };

  // Proje Kayıt Formu
  const [projeForm, setProjeForm] = useState({
    proje: '',
    parca_adi: '',
    parca_profil: '',
    proje_poz_no: '',
    kullanim_yeri: '',
    malzeme_turu: '',
    sac_kalinlik: '',
    bant_genisligi: '',
    bir_modulde: '',
    uzunluk: '',
    toplam_modul: '',
    miktar: '',
    birim_agirlik: '',
    agirlik: '',
    imalat_tonaj: ''
  });

  const handleProjeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await projectService.create({
        name: projeForm.proje,
        description: `${projeForm.parca_adi} - ${projeForm.proje_poz_no}`,
        status: 'in_progress',
        customer: projeForm.proje,
        details: {
          parca_adi: projeForm.parca_adi,
          parca_profil: projeForm.parca_profil,
          proje_poz_no: projeForm.proje_poz_no,
          kullanim_yeri: projeForm.kullanim_yeri,
          malzeme_turu: projeForm.malzeme_turu,
          sac_kalinlik: projeForm.sac_kalinlik,
          bant_genisligi: projeForm.bant_genisligi,
          bir_modulde: projeForm.bir_modulde,
          uzunluk: projeForm.uzunluk,
          toplam_modul: projeForm.toplam_modul,
          miktar: projeForm.miktar,
          birim_agirlik: projeForm.birim_agirlik,
          agirlik: projeForm.agirlik,
          imalat_tonaj: projeForm.imalat_tonaj
        }
      });
      setMessage({ type: 'success', text: '✅ Proje başarıyla kaydedildi!' });
      setProjeForm({
        proje: '',
        parca_adi: '',
        parca_profil: '',
        proje_poz_no: '',
        kullanim_yeri: '',
        malzeme_turu: '',
        sac_kalinlik: '',
        bant_genisligi: '',
        bir_modulde: '',
        uzunluk: '',
        toplam_modul: '',
        miktar: '',
        birim_agirlik: '',
        agirlik: '',
        imalat_tonaj: ''
      });
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Kayıt başarısız!' });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = user?.role === 'admin' || user?.role === 'operator';

  return (
    <div style={{ 
      padding: '2rem',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      minHeight: '100vh'
    }}>
      {/* Corporate Header with Abell Colors */}
      <div style={{ 
        marginBottom: '2rem', 
        textAlign: 'center',
        animation: 'fadeInDown 0.6s ease-out'
      }}>
        <h1 style={{ 
          fontSize: '3rem', 
          fontWeight: '800', 
          color: '#E40A04',
          margin: 0,
          letterSpacing: '3px',
          textTransform: 'uppercase',
          textShadow: '0 4px 12px rgba(228, 10, 4, 0.5)'
        }}>
          ANASAYFA
        </h1>
        <p style={{ 
          color: '#F1F1F1', 
          marginTop: '0.75rem',
          fontSize: '1rem',
          fontWeight: '600',
          letterSpacing: '2px',
          textTransform: 'uppercase'
        }}>
          Gelecek Bizimle Daha Parlak
        </p>
      </div>

      {/* Corporate Message */}
      {message.text && (
        <div style={{
          padding: '1.25rem 2rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          background: message.type === 'success' 
            ? 'rgba(16, 185, 129, 0.15)' 
            : 'rgba(228, 10, 4, 0.15)',
          border: `2px solid ${message.type === 'success' ? '#10b981' : '#E40A04'}`,
          color: message.type === 'success' ? '#10b981' : '#E40A04',
          textAlign: 'center',
          fontWeight: '600',
          fontSize: '1.05rem',
          boxShadow: `0 8px 24px ${message.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(228, 10, 4, 0.2)'}`,
          animation: 'slideInDown 0.5s ease-out',
          backdropFilter: 'blur(10px)'
        }}>
          {message.text}
        </div>
      )}

      {/* 4 Corporate Cards Layout */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Lojistik Card */}
        {canCreate(user, MODULES.HOMEPAGE_LOGISTICS) && (
        <CorporateFormCard 
          title="Lojistik" 
          icon="🚚"
          isActive={activeCard === 'lojistik'}
          onClick={() => setActiveCard('lojistik')}
        >
          <form onSubmit={handleSevkSubmit}>
            <CorporateSelectField 
              label="Proje" 
              required
              value={sevkForm.proje}
              onChange={(v) => setSevkForm({...sevkForm, proje: v, proje_parcasi: ''})}
              options={Object.keys(projectsWithParts)}
            />
            <CorporateSelectField 
              label="Proje parçası" 
              required
              value={sevkForm.proje_parcasi}
              onChange={(v) => setSevkForm({...sevkForm, proje_parcasi: v})}
              options={(projectsWithParts[sevkForm.proje] || []).map(p => p.part_name)}
              disabled={!sevkForm.proje}
            />
            <CorporateFormField label="Malzeme Türü (Otomatik)" 
              value={sevkForm.malzeme_turu}
              onChange={(v) => setSevkForm({...sevkForm, malzeme_turu: v})}
              disabled={true} />
            <CorporateFormField label="Uzunluk (Otomatik)" 
              value={sevkForm.uzunluk}
              onChange={(v) => setSevkForm({...sevkForm, uzunluk: v})}
              disabled={true} />
            <CorporateFormField label="Tarih" type="date" required
              value={sevkForm.tarih}
              onChange={(v) => setSevkForm({...sevkForm, tarih: v})} />
            <CorporateFormField label="Parça Adedi" type="number" required
              value={sevkForm.parca_adedi}
              onChange={(v) => setSevkForm({...sevkForm, parca_adedi: v})} />
            <CorporateFormField label="Çıkış Yeri" required
              value={sevkForm.cikis_yeri}
              onChange={(v) => setSevkForm({...sevkForm, cikis_yeri: v})} />
            <CorporateFormField label="Sevk Yeri" required
              value={sevkForm.sevk_yeri}
              onChange={(v) => setSevkForm({...sevkForm, sevk_yeri: v})} />
            <CorporateFormField label="İrsaliye No"
              value={sevkForm.irsaliye_numarasi}
              onChange={(v) => setSevkForm({...sevkForm, irsaliye_numarasi: v})} />
            <CorporateFormField label="Ağırlık (kg) - Otomatik" type="number" step="0.01"
              value={sevkForm.tonaj}
              onChange={(v) => setSevkForm({...sevkForm, tonaj: v})} />
            <CorporateButton disabled={!canSubmit || loading}>
              {loading ? '⏳ Kaydediliyor...' : '✓ Kaydet'}
            </CorporateButton>
          </form>
        </CorporateFormCard>
        )}

        {/* Üretim Card */}
        {canCreate(user, MODULES.HOMEPAGE_PRODUCTION) && (
        <CorporateFormCard 
          title="Üretim" 
          icon="🏭"
          isActive={activeCard === 'uretim'}
          onClick={() => setActiveCard('uretim')}
        >
          <form onSubmit={handleUretimSubmit}>
            <CorporateSelectField 
              label="Proje" 
              required
              value={uretimForm.proje}
              onChange={(v) => setUretimForm({...uretimForm, proje: v, proje_parcasi: ''})}
              options={Object.keys(projectsWithParts)}
            />
            <CorporateSelectField 
              label="Proje parçası" 
              required
              value={uretimForm.proje_parcasi}
              onChange={(v) => setUretimForm({...uretimForm, proje_parcasi: v})}
              options={(projectsWithParts[uretimForm.proje] || []).map(p => p.part_name)}
              disabled={!uretimForm.proje}
            />
            <CorporateFormField label="Malzeme Türü (Otomatik)" 
              value={uretimForm.malzeme_turu}
              onChange={(v) => setUretimForm({...uretimForm, malzeme_turu: v})}
              disabled={true} />
            <CorporateFormField label="Uzunluk (Otomatik)" 
              value={uretimForm.uzunluk}
              onChange={(v) => setUretimForm({...uretimForm, uzunluk: v})}
              disabled={true} />
            <CorporateFormField label="Tarih" type="date" required
              value={uretimForm.tarih}
              onChange={(v) => setUretimForm({...uretimForm, tarih: v})} />
            <CorporateFormField label="Parça Adedi" type="number" required
              value={uretimForm.parca_adedi}
              onChange={(v) => setUretimForm({...uretimForm, parca_adedi: v})} />
            <CorporateFormField label="Rulo No"
              value={uretimForm.rulo_numarasi}
              onChange={(v) => setUretimForm({...uretimForm, rulo_numarasi: v})} />
            <CorporateFormField label="Kanban No"
              value={uretimForm.kanban_numarasi}
              onChange={(v) => setUretimForm({...uretimForm, kanban_numarasi: v})} />
            <CorporateFormField label="Operatör"
              value={uretimForm.operator}
              onChange={(v) => setUretimForm({...uretimForm, operator: v})} />
            <CorporateFormField label="Ağırlık (kg) - Otomatik" type="number" step="0.01"
              value={uretimForm.tonaj}
              onChange={(v) => setUretimForm({...uretimForm, tonaj: v})} />
            <CorporateButton disabled={!canSubmit || loading}>
              {loading ? '⏳ Kaydediliyor...' : '✓ Kaydet'}
            </CorporateButton>
          </form>
        </CorporateFormCard>
        )}

        {/* Stok Giriş Card */}
        {canCreate(user, MODULES.HOMEPAGE_STOCK_IN) && (
        <CorporateFormCard 
          title="Stok Giriş" 
          icon="📦"
          isActive={activeCard === 'stok-giris'}
          onClick={() => setActiveCard('stok-giris')}
        >
          <form onSubmit={handleStokGirisSubmit}>
            <CorporateFormField label="Malzeme Adı" required
              value={stokGirisForm.malzeme_adi}
              onChange={(v) => setStokGirisForm({...stokGirisForm, malzeme_adi: v})} />
            <CorporateFormField label="Tarih" type="date" required
              value={stokGirisForm.tarih}
              onChange={(v) => setStokGirisForm({...stokGirisForm, tarih: v})} />
            <CorporateFormField label="Sipariş No"
              value={stokGirisForm.siparis_numarasi}
              onChange={(v) => setStokGirisForm({...stokGirisForm, siparis_numarasi: v})} />
            <CorporateFormField label="Rulo No"
              value={stokGirisForm.rulo_numarasi}
              onChange={(v) => setStokGirisForm({...stokGirisForm, rulo_numarasi: v})} />
            <CorporateFormField label="İrsaliye No"
              value={stokGirisForm.irsaliye_numarasi}
              onChange={(v) => setStokGirisForm({...stokGirisForm, irsaliye_numarasi: v})} />
            <CorporateFormField label="Miktar" type="number" step="0.01" required
              value={stokGirisForm.miktar}
              onChange={(v) => setStokGirisForm({...stokGirisForm, miktar: v})} />
            <CorporateFormField label="Şirket Adı"
              value={stokGirisForm.sirket_adi}
              onChange={(v) => setStokGirisForm({...stokGirisForm, sirket_adi: v})} />
            <CorporateFormField label="Fason Firma"
              value={stokGirisForm.fason_firma}
              onChange={(v) => setStokGirisForm({...stokGirisForm, fason_firma: v})} />
            <CorporateButton disabled={!canSubmit || loading}>
              {loading ? '⏳ Kaydediliyor...' : '✓ Kaydet'}
            </CorporateButton>
          </form>
        </CorporateFormCard>
        )}

        {/* Proje Card */}
        {canCreate(user, MODULES.HOMEPAGE_PROJECT) && (
        <CorporateFormCard 
          title="Proje" 
          icon="📁"
          isActive={activeCard === 'proje'}
          onClick={() => setActiveCard('proje')}
        >
          <form onSubmit={handleProjeSubmit}>
            <CorporateFormField label="Proje" required
              value={projeForm.proje}
              onChange={(v) => setProjeForm({...projeForm, proje: v})} />
            <CorporateFormField label="Parça Adı" required
              value={projeForm.parca_adi}
              onChange={(v) => setProjeForm({...projeForm, parca_adi: v})} />
            <CorporateFormField label="Parça-Profil"
              value={projeForm.parca_profil}
              onChange={(v) => setProjeForm({...projeForm, parca_profil: v})} />
            <CorporateFormField label="Proje Poz No"
              value={projeForm.proje_poz_no}
              onChange={(v) => setProjeForm({...projeForm, proje_poz_no: v})} />
            <CorporateFormField label="Malzeme Türü"
              value={projeForm.malzeme_turu}
              onChange={(v) => setProjeForm({...projeForm, malzeme_turu: v})} />
            <CorporateFormField label="Sac Kalınlık" type="number" step="0.01"
              value={projeForm.sac_kalinlik}
              onChange={(v) => setProjeForm({...projeForm, sac_kalinlik: v})} />
            <CorporateFormField label="Uzunluk" type="number"
              value={projeForm.uzunluk}
              onChange={(v) => setProjeForm({...projeForm, uzunluk: v})} />
            <CorporateFormField label="Toplam Adet" type="number" required
              value={projeForm.miktar}
              onChange={(v) => setProjeForm({...projeForm, miktar: v})} />
            <CorporateButton disabled={!canSubmit || loading}>
              {loading ? '⏳ Kaydediliyor...' : '✓ Kaydet'}
            </CorporateButton>
          </form>
        </CorporateFormCard>
        )}
      </div>

      {/* Stok Çıkış - Bottom Centered Card */}
      {canCreate(user, MODULES.HOMEPAGE_STOCK_OUT) && (
      <div style={{ maxWidth: '400px', margin: '0 auto' }}>
        <CorporateFormCard 
          title="Stok Çıkış" 
          icon="📤"
          isActive={activeCard === 'stok-cikis'}
          onClick={() => setActiveCard('stok-cikis')}
        >
          <form onSubmit={handleStokCikisSubmit}>
            <CorporateFormField label="Malzeme Adı" required
              value={stokCikisForm.malzeme_adi}
              onChange={(v) => setStokCikisForm({...stokCikisForm, malzeme_adi: v})} />
            <CorporateFormField label="Rulo No"
              value={stokCikisForm.rulo_numarasi}
              onChange={(v) => setStokCikisForm({...stokCikisForm, rulo_numarasi: v})} />
            <CorporateFormField label="Tarih" type="date" required
              value={stokCikisForm.tarih}
              onChange={(v) => setStokCikisForm({...stokCikisForm, tarih: v})} />
            <CorporateFormField label="Miktar" type="number" step="0.01" required
              value={stokCikisForm.miktar}
              onChange={(v) => setStokCikisForm({...stokCikisForm, miktar: v})} />
            <CorporateFormField label="Heat No"
              value={stokCikisForm.heat_numarasi}
              onChange={(v) => setStokCikisForm({...stokCikisForm, heat_numarasi: v})} />
            <CorporateButton disabled={!canSubmit || loading}>
              {loading ? '⏳ Kaydediliyor...' : '✓ Kaydet'}
            </CorporateButton>
          </form>
        </CorporateFormCard>
      </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shimmer {
          0% {
            background-position: -1000px 0;
          }
          100% {
            background-position: 1000px 0;
          }
        }
      `}</style>
    </div>
  );
};

// Corporate Form Card with Abell Colors
const CorporateFormCard = ({ title, icon, children, isActive, onClick }) => (
  <div 
    onClick={onClick}
    style={{
      background: 'rgba(51, 51, 51, 0.4)',
      backdropFilter: 'blur(10px)',
      borderRadius: '16px',
      overflow: 'hidden',
      boxShadow: isActive 
        ? '0 12px 40px rgba(228, 10, 4, 0.4), 0 0 0 3px #E40A04'
        : '0 8px 30px rgba(0,0,0,0.3)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isActive ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
      cursor: 'pointer',
      border: `1px solid ${isActive ? '#E40A04' : 'rgba(241, 241, 241, 0.2)'}`
    }}
  >
    <div style={{
      background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
      padding: '1.5rem',
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '200%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(241, 241, 241, 0.2), transparent)',
        animation: isActive ? 'shimmer 2s infinite' : 'none'
      }}></div>
      <h2 style={{
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#F1F1F1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        position: 'relative',
        letterSpacing: '1px',
        textTransform: 'uppercase'
      }}>
        <span style={{ fontSize: '2rem' }}>{icon}</span>
        {title}
      </h2>
    </div>
    <div style={{ padding: '1.75rem', background: 'rgba(0, 0, 0, 0.3)' }}>
      {children}
    </div>
  </div>
);

// Corporate Form Field with Abell Colors
const CorporateFormField = ({ label, value, onChange, type = 'text', step, required = false, disabled = false }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ 
      display: 'block', 
      marginBottom: '0.5rem', 
      fontWeight: '600', 
      fontSize: '0.85rem',
      color: '#F1F1F1',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      {label} {required && <span style={{ color: '#E40A04' }}>*</span>}
    </label>
    <input
      type={type}
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '0.75rem',
        background: disabled ? 'rgba(100, 100, 100, 0.3)' : 'rgba(241, 241, 241, 0.1)',
        border: '2px solid rgba(241, 241, 241, 0.2)',
        borderRadius: '8px',
        fontSize: '0.95rem',
        outline: 'none',
        transition: 'all 0.3s ease',
        color: disabled ? '#999' : '#F1F1F1',
        fontWeight: '500',
        cursor: disabled ? 'not-allowed' : 'text'
      }}
      onFocus={(e) => {
        if (!disabled) {
          e.target.style.background = 'rgba(241, 241, 241, 0.15)';
          e.target.style.border = '2px solid #E40A04';
          e.target.style.boxShadow = '0 4px 12px rgba(228, 10, 4, 0.3)';
        }
      }}
      onBlur={(e) => {
        if (!disabled) {
          e.target.style.background = 'rgba(241, 241, 241, 0.1)';
          e.target.style.border = '2px solid rgba(241, 241, 241, 0.2)';
          e.target.style.boxShadow = 'none';
        }
      }}
    />
  </div>
);

// Corporate Gradient Button with Abell Red
const CorporateButton = ({ disabled, children }) => (
  <button
    type="submit"
    disabled={disabled}
    style={{
      width: '100%',
      padding: '1rem',
      background: disabled 
        ? 'rgba(100, 100, 100, 0.3)' 
        : 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
      color: disabled ? '#666' : '#F1F1F1',
      border: disabled ? '2px solid rgba(100, 100, 100, 0.3)' : '2px solid #E40A04',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: '700',
      cursor: disabled ? 'not-allowed' : 'pointer',
      marginTop: '1rem',
      textTransform: 'uppercase',
      letterSpacing: '2px',
      boxShadow: disabled 
        ? 'none' 
        : '0 8px 24px rgba(228, 10, 4, 0.4)',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden'
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.target.style.transform = 'translateY(-2px)';
        e.target.style.boxShadow = '0 12px 32px rgba(228, 10, 4, 0.6)';
        e.target.style.background = 'linear-gradient(135deg, #B00803 0%, #E40A04 100%)';
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled) {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 8px 24px rgba(228, 10, 4, 0.4)';
        e.target.style.background = 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)';
      }
    }}
  >
    {children}
  </button>
);

// Corporate Select/Dropdown Field with Abell Colors
const CorporateSelectField = ({ label, value, onChange, options = [], required = false, disabled = false }) => (
  <div style={{ marginBottom: '1rem' }}>
    <label style={{ 
      display: 'block', 
      marginBottom: '0.5rem', 
      fontWeight: '600', 
      fontSize: '0.85rem',
      color: '#F1F1F1',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    }}>
      {label} {required && <span style={{ color: '#E40A04' }}>*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      disabled={disabled}
      style={{
        width: '100%',
        padding: '0.75rem',
        background: disabled ? 'rgba(100, 100, 100, 0.2)' : 'rgba(241, 241, 241, 0.1)',
        border: '2px solid rgba(241, 241, 241, 0.2)',
        borderRadius: '8px',
        fontSize: '0.95rem',
        outline: 'none',
        transition: 'all 0.3s ease',
        color: disabled ? '#666' : '#F1F1F1',
        fontWeight: '500',
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}
      onFocus={(e) => {
        if (!disabled) {
          e.target.style.background = 'rgba(241, 241, 241, 0.15)';
          e.target.style.border = '2px solid #E40A04';
          e.target.style.boxShadow = '0 4px 12px rgba(228, 10, 4, 0.3)';
        }
      }}
      onBlur={(e) => {
        if (!disabled) {
          e.target.style.background = 'rgba(241, 241, 241, 0.1)';
          e.target.style.border = '2px solid rgba(241, 241, 241, 0.2)';
          e.target.style.boxShadow = 'none';
        }
      }}
    >
      <option value="" style={{ background: '#2d2d2d', color: '#F1F1F1' }}>
        {disabled ? '-- Önce proje seçin --' : '-- Seçiniz --'}
      </option>
      {options.map((option, index) => (
        <option key={index} value={option} style={{ background: '#2d2d2d', color: '#F1F1F1' }}>
          {option}
        </option>
      ))}
    </select>
  </div>
);

export default HomePage;
